import { addTotals } from 'checkout/lib/utils/payments/totals'
import { createSelector } from 'reselect'
import { getBookingProtectionTotals } from './bookingProtection'
import getTotalsWithoutInsurance from 'checkout/selectors/payment/getTotalsWithoutInsurance'
import { getInsuranceTotals } from './insurance'
import { getLuxPlusSubscriptionTotals, getSubscriptionJoinFeeTotals } from './luxPlusSubscription'
import { isLastInstalment } from 'lib/payment/payInInstalmentsUtils'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { sum } from 'lib/array/arrayUtils'
import { getCharterUpgradeTotalPrice } from 'checkout/selectors/view/charterUpgrade'
import { getFlightBaggageTotalPrice } from 'checkout/selectors/view/flightBaggage'
import { getFlightSeatsTotalPrice } from 'checkout/selectors/view/flightSeats'
import { getCruiseAmendmentFeeAmount } from 'checkout/selectors/view/cruiseAmendment'
import getGroupPaymentShareContribution from './groupPayment/getGroupPaymentShareContribution'
import isGroupPaymentShareCheckout from './groupPayment/isGroupPaymentShareCheckout'

const getAncillaryPaymentTotals = createSelector(
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.checkout.cart.postPurchasePaymentSchedule,
  (state: App.State) => state.checkout.payment.paymentScheduleBalanceType,
  (state: App.State) => state.checkout.charterUpgrade,
  (state: App.State) => state.checkout.flightSeats,
  (state: App.State) => state.checkout.flightBaggage,
  (state: App.State) => state.checkout.cruiseAmendment,
  (state: App.State) => state.orders.flightDetails,
  getGroupPaymentShareContribution,
  isGroupPaymentShareCheckout,
  (postPurchaseMode, existingOrder, postPurchasePaymentSchedule, paymentScheduleBalanceType, charterUpgrade, flightSeats, flightBaggage, cruiseAmendment, flightDetails, groupPaymentContribution, isGroupPaymentShare): App.Checkout.ItemViewTotals | undefined => {
    if (isGroupPaymentShare && groupPaymentContribution) {
      return {
        price: groupPaymentContribution.amount,
        memberPrice: 0,
        taxesAndFees: 0,
        value: 0,
        memberValue: 0,
        surcharge: 0,
        extraGuestSurcharge: 0,
      }
    }

    if (postPurchaseMode === 'charter-upgrade' && charterUpgrade) {
      const totalPrice = getCharterUpgradeTotalPrice(charterUpgrade, flightDetails)
      return {
        price: totalPrice,
        memberPrice: 0,
        taxesAndFees: 0,
        value: 0,
        memberValue: 0,
        surcharge: 0,
        extraGuestSurcharge: 0,
      }
    }

    if (postPurchaseMode === 'flight-seats' && flightSeats) {
      return {
        price: getFlightSeatsTotalPrice(flightSeats),
        memberPrice: 0,
        taxesAndFees: 0,
        value: 0,
        memberValue: 0,
        surcharge: 0,
        extraGuestSurcharge: 0,
      }
    }

    const cruiseAmendmentFee = getCruiseAmendmentFeeAmount(cruiseAmendment)
    if (postPurchaseMode === 'cruise-amend-passengers') {
      return {
        price: cruiseAmendmentFee,
        memberPrice: 0,
        taxesAndFees: 0,
        value: 0,
        memberValue: 0,
        surcharge: 0,
        extraGuestSurcharge: 0,
      }
    }

    if (postPurchaseMode === 'flight-baggage' && flightBaggage) {
      return {
        price: getFlightBaggageTotalPrice(flightBaggage),
        memberPrice: 0,
        taxesAndFees: 0,
        value: 0,
        memberValue: 0,
        surcharge: 0,
        extraGuestSurcharge: 0,
      }
    }

    if (existingOrder) {
      if (postPurchaseMode === 'deposit-balance' && existingOrder.depositDetails) {
        return {
          price: existingOrder.depositDetails.balance_amount,
          memberPrice: 0,
          taxesAndFees: 0,
          value: 0,
          memberValue: 0,
          surcharge: 0,
          extraGuestSurcharge: 0,
        }
      } else if (existingOrder.instalmentDetails && (postPurchaseMode === 'instalment-balance' || isLastInstalment(existingOrder.instalmentDetails))) {
        return {
          price: existingOrder.instalmentDetails.balance_amount,
          memberPrice: 0,
          taxesAndFees: 0,
          value: 0,
          memberValue: 0,
          surcharge: 0,
          extraGuestSurcharge: 0,
        }
      } else if (postPurchaseMode === 'instalment' && existingOrder.instalmentDetails) {
        return {
          price: existingOrder.instalmentDetails.per_instalment_amount,
          memberPrice: 0,
          taxesAndFees: 0,
          value: 0,
          memberValue: 0,
          surcharge: 0,
          extraGuestSurcharge: 0,
        }
      } else if (postPurchaseMode === 'reserve-balance' && existingOrder.reserveForZeroDetails) {
        return {
          price: existingOrder.reserveForZeroDetails.payableAmount,
          memberPrice: 0,
          taxesAndFees: 0,
          value: 0,
          memberValue: 0,
          surcharge: 0,
          extraGuestSurcharge: 0,
        }
      } else if (postPurchaseMode === 'reserve-for-zero-deposit' && postPurchasePaymentSchedule?.charges[0]) {
        return {
          price: postPurchasePaymentSchedule.charges[0].totalAmount,
          memberPrice: 0,
          taxesAndFees: 0,
          value: 0,
          memberValue: 0,
          surcharge: 0,
          extraGuestSurcharge: 0,
        }
      } else if (postPurchaseMode === 'custom-offer' && existingOrder.customOfferItems.length > 0) {
        return {
          price: parseFloat(existingOrder.customOfferItems[0].total),
          memberPrice: 0,
          taxesAndFees: 0,
          value: 0,
          memberValue: 0,
          surcharge: 0,
          extraGuestSurcharge: 0,
        }
      } else if (postPurchaseMode === 'custom-offer-v2' && existingOrder.customOfferItems.length > 0) {
        return {
          price: existingOrder.total,
          memberPrice: 0,
          taxesAndFees: 0,
          value: 0,
          memberValue: 0,
          surcharge: 0,
          extraGuestSurcharge: 0,
        }
      } else if (postPurchaseMode === 'payment-schedule-balance' && existingOrder.paymentScheduleDetails) {
        if (paymentScheduleBalanceType === 'full_balance') {
          // Calculate the full balance (all pending/failed charges)
          const fullBalanceAmount = sum(existingOrder.paymentScheduleDetails.charges.filter(charge => charge.status === 'pending' || charge.status === 'failed'), (charge: App.PaymentSchedule) => charge.totalAmount)

          return {
            price: fullBalanceAmount,
            memberPrice: 0,
            taxesAndFees: 0,
            value: 0,
            memberValue: 0,
            surcharge: 0,
            extraGuestSurcharge: 0,
          }
        } else {
          // Default to next charge only
          const next_payment = [...existingOrder.paymentScheduleDetails.charges]
            .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
            .find(schedule => schedule.status === 'pending' || schedule.status === 'failed')

          if (!next_payment) {
            return
          }

          return {
            price: next_payment.totalAmount,
            memberPrice: 0,
            taxesAndFees: 0,
            value: 0,
            memberValue: 0,
            surcharge: 0,
            extraGuestSurcharge: 0,
          }
        }
      }
    }

    return
  },
)

const getCheckoutTotals = createSelector(
  (state: App.State) => getTotalsWithoutInsurance(state),
  (state: App.State) => getInsuranceTotals(state),
  (state: App.State) => getAncillaryPaymentTotals(state),
  (state: App.State) => getBookingProtectionTotals(state),
  (state: App.State) => getLuxPlusSubscriptionTotals(state),
  (state: App.State) => getSubscriptionJoinFeeTotals(state),
  (totals, insurance, ancillaryTotals, bookingProtection, luxPlusTotal, joinTotal): App.WithDataStatus<App.Checkout.ItemViewTotals> => {
    if (ancillaryTotals) {
      return { hasRequiredData: true, data: ancillaryTotals }
    } else {
      return addTotals([totals, insurance, bookingProtection, luxPlusTotal, joinTotal])
    }
  },
)

export default getCheckoutTotals
